import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import type { DetrixSession } from '../types';
import { useSessionUiStore } from '../store/useSessionUiStore';
import { buildErrorNarrativeLines } from '../utils/sessionErrorSignals';

function cn(...xs: (string | false | undefined)[]) {
  return xs.filter(Boolean).join(' ');
}

/** Collapsible “What happened” for error selections; reads tab/selection from {@link useSessionUiStore}. */
export const ErrorNarrative: React.FC<{ session: DetrixSession }> = ({ session }) => {
  const { activeTab, selectedId } = useSessionUiStore();
  const [expanded, setExpanded] = useState(true);

  const lines = useMemo(
    () => buildErrorNarrativeLines(session, activeTab, selectedId),
    [session, activeTab, selectedId],
  );

  useEffect(() => {
    setExpanded(true);
  }, [selectedId, activeTab, lines.length]);

  if (lines.length === 0) return null;

  const bullets = lines.slice(1);

  return (
    <div className="shrink-0 border-b border-[var(--narrative-border)] bg-gradient-to-b from-[var(--narrative-bg-top)] to-[var(--narrative-bg-bottom)]">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="flex w-full min-w-0 items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-[var(--narrative-hover)]"
      >
        {expanded ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-[var(--narrative-header-icon)]" aria-hidden />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-[var(--narrative-header-icon)]" aria-hidden />
        )}
        <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-[var(--narrative-header-icon)]" aria-hidden />
        <span className="min-w-0 text-[10px] font-bold uppercase tracking-[0.14em] text-[var(--narrative-header-text)]">
          What happened
        </span>
      </button>
      {expanded ? (
        <div className="max-h-[min(40vh,15rem)] min-h-0 overflow-y-auto overflow-x-hidden border-t border-[var(--narrative-divider)] px-3 py-2">
          <ol className="list-none space-y-1.5 pl-0">
            {bullets.map((line, i) => (
              <li
                key={i}
                className={cn(
                  'relative break-words pl-3 text-[11px] leading-snug text-[var(--narrative-body-text)] before:absolute before:left-0 before:top-2 before:h-1.5 before:w-1.5 before:rounded-full before:bg-[var(--narrative-bullet)]',
                  i === 0 && 'font-medium text-[var(--narrative-first-line)]',
                )}
              >
                {line}
              </li>
            ))}
          </ol>
        </div>
      ) : null}
    </div>
  );
};
